import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/stores/appStore';
import { analyticsApi } from '@/lib/api';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { Loader2, Sparkles, TrendingUp, TrendingDown, CalendarClock, FileText, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Last 12 complete months, newest first, as YYYY-MM + display label. */
function monthOptions(): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];
  const d = new Date();
  for (let i = 1; i <= 12; i++) {
    const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    out.push({
      value: `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, '0')}`,
      label: m.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    });
  }
  return out;
}

const fmt = (n: number) => n.toLocaleString();

export function MonthlyReport() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].value);

  const { data, isLoading } = useQuery({
    queryKey: ['monthly-report', currentWorkspace?.id, month],
    queryFn: async () => {
      const token = await getToken();
      return analyticsApi.report(token!, currentWorkspace!.id, month);
    },
    enabled: !!currentWorkspace,
  });

  const narrative = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return analyticsApi.reportNarrative(token!, currentWorkspace!.id, month);
    },
  });

  // Share a white-label, token-gated report page with the client. The current
  // narrative (if generated) is frozen into the share.
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const share = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return analyticsApi.shareReport(
        token!,
        currentWorkspace!.id,
        month,
        narrative.data?.narrative
      );
    },
    onSuccess: (resp) => {
      setShareUrl(resp.share.url);
      navigator.clipboard.writeText(resp.share.url).catch(() => {});
    },
  });

  const report = data?.report;
  const monthLabel = months.find((m) => m.value === month)?.label ?? month;

  const tiles = report
    ? [
        { label: 'Posts Published', value: report.publishedCount },
        { label: 'Impressions', value: report.totals.impressions },
        { label: 'Engagements', value: report.totals.engagements },
        { label: 'Likes', value: report.totals.likes },
        { label: 'Comments', value: report.totals.comments },
        { label: 'Shares', value: report.totals.shares },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
            <FileText className="h-4 w-4 text-accent" />
            Monthly Report
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Recap, best performers, growth, and what's coming next for {monthLabel}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              narrative.reset();
              setShareUrl(null);
              share.reset();
            }}
            className="bg-input border border-border rounded-m px-3 py-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => share.mutate()}
            disabled={share.isPending || !report}
            title="Create a branded, login-free report link for your client (90-day expiry). Generate the narrative first to include it."
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-tertiary text-foreground ring-1 ring-border rounded-m hover:bg-tertiary/70 disabled:opacity-50 transition-colors"
          >
            {share.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Share2 className="h-3.5 w-3.5" />
            )}
            Share with client
          </button>
        </div>
      </div>

      {shareUrl && (
        <p className="text-xs text-success -mt-3">
          Client link copied to clipboard — valid for 90 days:{' '}
          <a href={shareUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline break-all">
            {shareUrl}
          </a>
        </p>
      )}
      {share.isError && (
        <p className="text-xs text-destructive -mt-3">
          {(share.error as Error).message || 'Could not create the share link.'}
        </p>
      )}

      {isLoading || !report ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          {/* Headline numbers */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {tiles.map((t) => (
              <div key={t.label} className="bg-card rounded-l p-4 ring-1 ring-hairline shadow-kumo-m">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  {t.label}
                </p>
                <p className="font-display text-2xl font-semibold text-foreground mt-1 tabular-nums">
                  {fmt(t.value)}
                </p>
              </div>
            ))}
          </div>

          {/* AI narrative */}
          <div className="bg-card rounded-l ring-1 ring-hairline shadow-kumo-m p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-sm font-semibold text-foreground">
                The story of {monthLabel}
              </h3>
              <button
                onClick={() => narrative.mutate()}
                disabled={narrative.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-accent/15 text-accent ring-1 ring-accent/30 rounded-m hover:bg-accent/25 disabled:opacity-50 transition-colors"
              >
                {narrative.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                {narrative.data ? 'Regenerate' : 'Write the narrative'}
              </button>
            </div>
            {narrative.error ? (
              <p className="text-xs text-destructive">
                {(narrative.error as Error).message || 'Narrative generation failed — try again.'}
              </p>
            ) : narrative.data ? (
              <div className="text-sm text-foreground/90 leading-relaxed space-y-3">
                {narrative.data.narrative.split(/\n\n+/).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Let AI read the month's numbers and explain what worked, why, and what to do next —
                written in {currentWorkspace?.name}'s brand context.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Follower growth */}
            <div className="bg-card rounded-l ring-1 ring-hairline shadow-kumo-m p-5">
              <h3 className="font-display text-sm font-semibold text-foreground mb-3">
                Follower growth
              </h3>
              {report.followerGrowth.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Daily history starts accruing from the first analytics sync after this feature
                  shipped — growth will appear here from next month.
                </p>
              ) : (
                <div className="space-y-2.5">
                  {report.followerGrowth.map((g) => (
                    <div key={g.platform} className="flex items-center gap-3">
                      <span className="flex items-center gap-1.5 text-xs text-foreground w-28 shrink-0">
                        <PlatformIcon platform={g.platform} variant="glyph" size="xs" />
                        {PLATFORMS[g.platform as keyof typeof PLATFORMS]?.label ?? g.platform}
                      </span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {fmt(g.start)} → {fmt(g.end)}
                      </span>
                      <span
                        className={cn(
                          'flex items-center gap-0.5 text-xs font-semibold tabular-nums ml-auto',
                          g.delta >= 0 ? 'text-success' : 'text-destructive'
                        )}
                      >
                        {g.delta >= 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        {g.delta >= 0 ? '+' : ''}
                        {fmt(g.delta)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Per-platform */}
            <div className="bg-card rounded-l ring-1 ring-hairline shadow-kumo-m p-5">
              <h3 className="font-display text-sm font-semibold text-foreground mb-3">
                By platform
              </h3>
              {report.platforms.length === 0 ? (
                <p className="text-xs text-muted-foreground">No platform metrics recorded this month.</p>
              ) : (
                <div className="space-y-2.5">
                  {report.platforms.map((p) => (
                    <div key={p.platform} className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1.5 text-foreground w-28 shrink-0">
                        <PlatformIcon platform={p.platform} variant="glyph" size="xs" />
                        {PLATFORMS[p.platform as keyof typeof PLATFORMS]?.label ?? p.platform}
                      </span>
                      <span className="text-muted-foreground tabular-nums">
                        {p.posts} posts · {fmt(p.impressions)} impressions · {fmt(p.engagements)}{' '}
                        engagements
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Best performers */}
          <div className="bg-card rounded-l ring-1 ring-hairline shadow-kumo-m p-5">
            <h3 className="font-display text-sm font-semibold text-foreground mb-3">
              Best performing posts
            </h3>
            {report.topPosts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No ranked posts for this month.</p>
            ) : (
              <div className="space-y-2">
                {report.topPosts.map((p, i) => (
                  <div key={p.id} className="flex items-start gap-3 p-3 bg-muted/40 ring-1 ring-border/50 rounded-lg">
                    <span className="text-sm font-bold text-muted-foreground w-6 shrink-0">
                      #{i + 1}
                    </span>
                    <p className="text-xs text-foreground line-clamp-2 flex-1 min-w-0">
                      {p.content || <span className="italic text-muted-foreground">(no caption)</span>}
                    </p>
                    <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                      {fmt(p.engagements)} eng · {fmt(p.impressions)} impr
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recap */}
            <div className="bg-card rounded-l ring-1 ring-hairline shadow-kumo-m p-5">
              <h3 className="font-display text-sm font-semibold text-foreground mb-3">
                What was posted ({report.recap.length})
              </h3>
              {report.recap.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nothing published via Enflux this month.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {report.recap.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground shrink-0 tabular-nums w-14">
                        {p.published_at
                          ? new Date(p.published_at).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                            })
                          : '—'}
                      </span>
                      <p className="text-foreground line-clamp-1 flex-1 min-w-0">{p.content}</p>
                      {p.failed_count > 0 && (
                        <span className="text-destructive shrink-0">{p.failed_count} failed</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming preview */}
            <div className="bg-card rounded-l ring-1 ring-hairline shadow-kumo-m p-5">
              <h3 className="font-display text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5 text-accent" />
                Coming up next
              </h3>
              {report.upcoming.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nothing scheduled yet — fill the queue!</p>
              ) : (
                <div className="space-y-2">
                  {report.upcoming.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground shrink-0 tabular-nums w-24">
                        {new Date(p.scheduled_at).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                      <p className="text-foreground line-clamp-1 flex-1 min-w-0">{p.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
